import React, { useEffect, useMemo, useState } from "react";
import { Button, Group, Select, TextInput, CopyButton } from "@mantine/core";
import { QRCodeSVG } from "qrcode.react";
import "../Admin/Event/AdminEvent.css";

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface TicketRowDto {
    ticketPersonId: number;
    collectionName: string;
    maxDrinks: number;
    usedDrinks: number;
    remainingDrinks: number;
    createdAt: string;

    // backend kan sende den - ellers bygger vi den selv
    qrUrl?: string;
}

const AdminTicketList: React.FC = () => {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [collections, setCollections] = useState<string[]>([]);
    const [collectionName, setCollectionName] = useState<string | null>(null);

    const [tickets, setTickets] = useState<TicketRowDto[]>([]);
    const [search, setSearch] = useState<string>("");

    const [error, setError] = useState<string | null>(null);

    // base URL til QR/link (brug samme host som siden k�rer p�)
    const baseUrl = useMemo(() => window.location.origin, []);

    // ---------------- HENT EVENTS ----------------
    useEffect(() => {
        fetch("/api/events")
            .then((r) => (r.ok ? r.json() : []))
            .then((data) => setEvents(Array.isArray(data) ? data : []))
            .catch(() => setError("Kunne ikke hente events"));
    }, []);

    // ---------------- HENT COLLECTIONS ----------------
    const fetchCollections = async (eid: number) => {
        if (!eid) return;
        try {
            const res = await fetch(`/api/admin/drink-tickets/collections?eventId=${eid}`);
            if (!res.ok) return;
            const data = await res.json();
            setCollections(Array.isArray(data) ? data : []);
        } catch {
            // ignore
        }
    };

    // ---------------- HENT TICKETS ----------------
    const fetchTickets = async (eid: number, coll?: string | null) => {
        if (!eid) return;
        setError(null);

        let url = `/api/admin/drink-tickets/tickets?eventId=${eid}`;
        if (coll) url += `&collectionName=${encodeURIComponent(coll)}`;

        try {
            const res = await fetch(url);
            const text = await res.text();
            if (!res.ok) throw new Error(text || "Kunne ikke hente billetter");
            const data = text ? JSON.parse(text) : [];
            setTickets(Array.isArray(data) ? data : []);
        } catch (e: any) {
            setError(e.message || "Ukendt fejl");
            setTickets([]);
        }
    };

    // n�r event skifter
    useEffect(() => {
        setCollectionName(null);
        setCollections([]);
        setTickets([]);
        setSearch("");
        setError(null);

        if (!eventId) return;

        fetchCollections(eventId);
        fetchTickets(eventId, null);
    }, [eventId]);

    // n�r collection skifter
    useEffect(() => {
        if (!eventId) return;
        fetchTickets(eventId, collectionName);
    }, [collectionName]);

    const filteredTickets = useMemo(() => {
        const s = search.trim().toLowerCase();
        if (!s) return tickets;

        return tickets.filter((t) => {
            const idMatch = String(t.ticketPersonId).includes(s);
            const collMatch = (t.collectionName || "").toLowerCase().includes(s);
            return idMatch || collMatch;
        });
    }, [tickets, search]);

    const totals = useMemo(() => {
        return filteredTickets.reduce(
            (acc, t) => {
                acc.max += t.maxDrinks || 0;
                acc.used += t.usedDrinks || 0;
                acc.remaining += t.remainingDrinks || 0;
                return acc;
            },
            { max: 0, used: 0, remaining: 0 }
        );
    }, [filteredTickets]);

    const buildTicketUrl = (ticketPersonId: number) => {
        // rute: /drinks/ticket/:eventId/:ticketPersonId
        return `${baseUrl}/drinks/ticket/${eventId}/${ticketPersonId}`;
    };

    return (
        <div className="form-container">
            <h2>Billetter / fiktive personer</h2>

            {error && <p className="error-message">{error}</p>}

            <label>Event *</label>
            <Select
                placeholder="V�lg event"
                value={eventId ? String(eventId) : null}
                onChange={(val) => setEventId(val ? Number(val) : 0)}
                data={events.map((e) => ({
                    value: String(e.eventID),
                    label: e.eventName || `Event ${e.eventID}`,
                }))}
                searchable
                clearable
                nothingFoundMessage="Ingen events"
            />

            {eventId > 0 && (
                <>
                    <label style={{ marginTop: 12 }}>Samling</label>
                    <Select
                        placeholder="Alle samlinger"
                        clearable
                        value={collectionName}
                        onChange={setCollectionName}
                        data={collections.map((c) => ({ value: c, label: c }))}
                        searchable
                        nothingFoundMessage="Ingen samlinger"
                    />

                    <label style={{ marginTop: 12 }}>S�g (ticket id / samling)</label>
                    <TextInput
                        placeholder="Fx 123 eller 'Fredag'"
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                    />
                </>
            )}

            {eventId > 0 && filteredTickets.length > 0 && (
                <>
                    <div style={{ marginTop: 18, fontWeight: "bold" }}>
                        Totaler (filtreret):
                        <div>Max: {totals.max}</div>
                        <div>Brugt: {totals.used}</div>
                        <div>Tilbage: {totals.remaining}</div>
                    </div>

                    <table
                        style={{
                            marginTop: 20,
                            width: "100%",
                            borderCollapse: "collapse",
                        }}
                    >
                        <thead>
                            <tr style={{ background: "#f4f4f4" }}>
                                <th style={thLeft}>Ticket</th>
                                <th style={thLeft}>Samling</th>
                                <th style={thCenter}>Max</th>
                                <th style={thCenter}>Brugt</th>
                                <th style={thCenter}>Tilbage</th>
                                <th style={thCenter}>QR</th>
                                <th style={thCenter}>Link</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTickets.map((t) => {
                                const url = t.qrUrl
                                    ? `${baseUrl}${t.qrUrl.startsWith("/") ? "" : "/"}${t.qrUrl}`
                                    : buildTicketUrl(t.ticketPersonId);

                                return (
                                    <tr key={t.ticketPersonId}>
                                        <td style={tdLeft}>#{t.ticketPersonId}</td>
                                        <td style={tdLeft}>{t.collectionName}</td>
                                        <td style={tdCenter}>{t.maxDrinks}</td>
                                        <td style={tdCenter}>{t.usedDrinks}</td>
                                        <td style={{ ...tdCenter, fontWeight: "bold" }}>
                                            {t.remainingDrinks}
                                        </td>
                                        <td style={tdCenter}>
                                            <QRCodeSVG value={url} size={64} />
                                        </td>
                                        <td style={tdCenter}>
                                            <Group justify="center" gap="xs">
                                                <CopyButton value={url}>
                                                    {({ copied, copy }) => (
                                                        <Button size="xs" onClick={copy}>
                                                            {copied ? "Kopieret" : "Kopi�r"}
                                                        </Button>
                                                    )}
                                                </CopyButton>

                                                <Button
                                                    size="xs"
                                                    variant="light"
                                                    onClick={() => window.open(url, "_blank")}
                                                >
                                                    �bn
                                                </Button>
                                            </Group>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}

            {eventId > 0 && filteredTickets.length === 0 && (
                <p style={{ marginTop: 15 }}>Ingen billetter fundet</p>
            )}
        </div>
    );
};

const thBase: React.CSSProperties = {
    padding: "10px",
    borderBottom: "2px solid #ddd",
    whiteSpace: "nowrap",
};

const thLeft: React.CSSProperties = { ...thBase, textAlign: "left" };
const thCenter: React.CSSProperties = { ...thBase, textAlign: "center" };

const tdBase: React.CSSProperties = {
    padding: "8px",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
};

const tdLeft: React.CSSProperties = { ...tdBase, textAlign: "left" };
const tdCenter: React.CSSProperties = { ...tdBase, textAlign: "center" };

export default AdminTicketList;